import React, { createContext, useState, useEffect } from "react";
import axiosInstance from "../services/axiosInstance";
import { jwtDecode } from "jwt-decode";
import Cookies from "js-cookie";

// Create Context
const UserContext = createContext();

const cookieOptions = {
  sameSite: "Lax",
  domain: process.env.REACT_APP_DLS_COOKIE_DOMAIN,
  httpOnly: false,
};

// UserContext Provider Component
const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(Cookies.get("accessToken"));
  const [refreshToken, setRefreshToken] = useState(Cookies.get("refreshToken"));

  // Function to refresh access token
  const refreshAccessToken = async () => {
    try {
      const response = await axiosInstance.post("/api/auth/refreshToken", {
        refreshToken,
      });
      const newAccessToken = response.token;
      Cookies.set("accessToken", newAccessToken, cookieOptions);
      setAccessToken(newAccessToken);
    } catch (error) {
      clearUserData();
      console.error("Failed to refresh token:", error);
    }
  };

  // Manage token refresh
  useEffect(() => {
    if (accessToken && accessToken !== "undefined") {
      const decodedToken = jwtDecode(accessToken);
      const currentTime = Date.now() / 1000;
      if (decodedToken.exp < currentTime) {
        clearUserData();
        return;
      }
      const timeToExpire = decodedToken.exp - currentTime;

      if (timeToExpire > 120) {
        const timer = setTimeout(
          refreshAccessToken,
          (timeToExpire - 120) * 1000
        );
        return () => clearTimeout(timer);
      } else {
        refreshAccessToken();
      }
    }
  }, [accessToken, refreshToken]);

  // Load user data from Cookies
  useEffect(() => {
    const fetchUserData = () => {
      const storedData = Cookies.get("userData");
      if (storedData) {
        setUserData(JSON.parse(storedData));
      }
      setLoading(false);
    };

    fetchUserData();
  }, []);

  // Function to save user data to Cookies
  const saveUserData = (data) => {
    setUserData(data);
    Cookies.set("userData", JSON.stringify(data), cookieOptions);
  };

  const saveToken = (access, refresh) => {
    Cookies.set("accessToken", access, cookieOptions);
    Cookies.set("refreshToken", refresh, cookieOptions);
    setAccessToken(access);
    setRefreshToken(refresh);
  };

  // Clear user data
  const removeCookie = (name) => {
    Cookies.remove(name, {
      domain: cookieOptions.domain,
      path: "/",
    });
    Cookies.remove(name, { path: "/" });
    Cookies.remove(name);
  };

  const clearUserData = () => {
    setUserData(null);
    setAccessToken(null);
    setRefreshToken(null);

    removeCookie("userData");
    removeCookie("accessToken");
    removeCookie("refreshToken");
  };

  return (
    <UserContext.Provider
      value={{
        userData,
        saveUserData,
        clearUserData,
        loading,
        saveToken,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export { UserContext, UserProvider };
